/**
 * Timeline state contexts for Steve Render Service.
 *
 * Provides the global frame/playback state consumed by all
 * scene, video, and audio components.
 *
 * Written from scratch for Steve.AI.
 */

import { createContext, useContext, useMemo } from 'react';

/**
 * Readable timeline state — frame position and playback settings.
 */
export const TimelineContext = createContext({
  frame: 0,
  playing: false,
  playbackRate: 1,
  rootId: '',
  imperativePlaying: { current: false },
  setPlaybackRate: () => {},
  audioAndVideoTags: { current: [] },
  loop: false,
  setLoop: () => {},
  reverse: false,
  setReverse: () => {},
  muted: false,
  setMuted: () => {},
  buffering: false,
  setBuffering: () => {},
});

/**
 * Writable timeline state — frame and playing setters.
 */
export const SetTimelineContext = createContext({
  setFrame: () => {},
  setPlaying: () => {},
});

/**
 * Returns the current absolute frame number.
 */
export const useTimelinePosition = () => {
  const { frame } = useContext(TimelineContext);
  return frame;
};

/**
 * Returns the setFrame function.
 */
export const useTimelineSetFrame = () => {
  const { setFrame } = useContext(SetTimelineContext);
  return setFrame;
};

/**
 * Returns [playing, setPlaying, imperativePlaying].
 */
export const usePlayingState = () => {
  const { playing, imperativePlaying } = useContext(TimelineContext);
  const { setPlaying } = useContext(SetTimelineContext);

  return useMemo(
    () => [playing, setPlaying, imperativePlaying],
    [playing, setPlaying, imperativePlaying]
  );
};
